import { RefObject, useCallback, useEffect, useRef } from 'react';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';

// Scrolls to the first selected item when entering selection mode and to the last
// selected item when leaving selection mode. Helps reduce layout shifts during
// selection mode UI changes.
export function useCenterOnSelectionModeToggle(
  scrollableRef: RefObject<HTMLElement | null>,
  containerRef: RefObject<HTMLElement | null>,
  selected: number[],
  hasSelection: boolean
) {
  // we have these refs to avoid unnecessary re-renders
  const prevHasSelectionRef = useRef<boolean>(false);
  const lastSelectedIndexRef = useRef<number | null>(null);

  const scrollItemIntoView = useCallback(
    (el: HTMLElement) => {
      const scrollableEl = scrollableRef.current;
      if (!scrollableEl) {
        return;
      }
      const scrollableRect = scrollableEl.getBoundingClientRect();
      const itemRect = el.getBoundingClientRect();
      const delta = itemRect.top - scrollableRect.top;
      const target =
        scrollableEl.scrollTop +
        delta -
        (scrollableRect.height / 2 - itemRect.height / 2);
      scrollableEl.scrollTo({ top: target, behavior: 'smooth' });
    },
    [scrollableRef]
  );

  const scrollToCenter = useCallback(
    (itemIndex: number) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const targetEl = container.children.item(itemIndex) as HTMLElement | null;
      if (targetEl) {
        scrollItemIntoView(targetEl);
      }
    },
    [containerRef, scrollItemIntoView]
  );

  // Keep track of the last selected index to scroll to when leaving selection mode
  useEffect(() => {
    if (selected.length > 0) {
      lastSelectedIndexRef.current = selected[selected.length - 1];
    }
  }, [selected]);

  // Scroll to the first or last selected item when entering or leaving selection mode
  useEffect(() => {
    const enteringSelection = hasSelection && !prevHasSelectionRef.current;
    const leavingSelection = !hasSelection && prevHasSelectionRef.current;
    prevHasSelectionRef.current = hasSelection;

    if (!enteringSelection && !leavingSelection) {
      return;
    }

    let targetIndex: number | null = null;

    if (enteringSelection) {
      targetIndex = selected[0];
    } else if (leavingSelection) {
      targetIndex = lastSelectedIndexRef.current;
      lastSelectedIndexRef.current = null;
    }

    if (targetIndex !== null) {
      // Yield to main, then one more RAF to allow layout to fully settle after selection-mode height changes
      yieldToMain(() => {
        requestAnimationFrame(() => scrollToCenter(targetIndex as number));
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelection]);
}
